/**
 * SERVICE TAMPON PERMETANT DE GERER LES ITEMS DU PANIER ANGULARJS
 */
import { EventEmitter, Injectable, Output } from '@angular/core';

import { Accessory, CartModel, Product, ProductFactory, QUOTE_CONTEXTS, QuoteModel } from '@bytel/bytel-sales';
import { AddFundingService, CartCheckoutService, CheckoutCartModel, FUNDING_ENUM, UserService } from '@bytel/checkout';

import { PRODUCT_TYPE } from '@base/product-type.enum';

import { JsonProduct } from '@catalog/products/interface/context';
import { Faim } from '@catalog/products/subscription/plan/faim';

import { RenewContextService } from '@context/child/renew-context.service';

import { FaimSensation } from '@app/catalog/products/subscription/plan/premium/faim_sensation';
import { Sensation } from '@app/catalog/products/subscription/plan/premium/sensation';

import { ConfigurationService } from '@common';
import { Observable, Subject, lastValueFrom, of } from 'rxjs';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { catchError, mergeMap } from 'rxjs/operators';

import { UniversalStorageService } from '../../../base/services/universal-storage.service';
import { Simo } from '../../../catalog/products/subscription/plan/simo';
import { Sowo } from '../../../catalog/products/subscription/plan/sowo';
import { CartRedirect, CheckoutQuote } from '../../interfaces/checkout';

@Injectable({
    providedIn: 'root',
})
export class CartHelperService {
    @Output() updatedCart: EventEmitter<boolean> = new EventEmitter();
    public planInCart: JsonProduct;
    public phoneInCart: JsonProduct;
    public quote: CheckoutQuote;
    public noPlanChosenSubj$: Subject<boolean> = new Subject<boolean>();
    public isUpdatingCartSubj$: BehaviorSubject<{ updating: boolean; productsBeingAddedGencodes: string[] }> =
        new BehaviorSubject<{ updating: boolean; productsBeingAddedGencodes: string[] }>({
            updating: false,
            productsBeingAddedGencodes: [],
        });

    protected cartModel: CheckoutCartModel;

    private _isRenew: boolean = false;

    constructor(
        private universalStorage: UniversalStorageService,
        private configService: ConfigurationService,
        private cartCheckoutService: CartCheckoutService,
        private userService: UserService,
        private addFundingService: AddFundingService,
    ) {
        this.cartModel = new CheckoutCartModel();
        this.cartCheckoutService.initCart(this.cartModel);
        this.quote = this.getQuote();
    }

    public getCartRedirect(): CartRedirect {
        return {
            url: '/panier',
            isSpa: false, // newCart
        };
    }

    public getQuote(): CheckoutQuote {
        let cart: CheckoutQuote = null;

        const currentQuote: QuoteModel = this.getCheckoutQuote();

        cart = {
            items: [],
            isRenew: currentQuote.isRenewal(),
            option_salable: [],
        };

        currentQuote.products.forEach((product) => {
            const data = { ...product.data, is_pro: !!product.isPro };
            if (['option', 'option_salable', 'offer_partner'].includes(product.data.type_id)) {
                cart.option_salable.push(data);
            } else {
                if (ProductFactory.Is(product, 'Equipment') || ProductFactory.Is(product, 'Subscription')) {
                    const item: { source: any; params?: any } = { source: data };
                    let type: string;
                    switch (true) {
                        case ProductFactory.Is(product, 'Plan'):
                            type = 'plan';
                            break;
                        case ProductFactory.Is(product, 'Box'):
                            type = 'box';
                            break;
                        case ProductFactory.Is(product, 'Phone'):
                            type = 'phone';
                            break;
                        default:
                            type = product.data.type || product.data.type_id;
                            break;
                    }

                    item.source.type = type;
                    item.source.id = item.source.entity_id;
                    cart.items.push(item);
                }
            }
        });

        return cart;
    }

    // @todo utiliser pour cross-sell pour actualiser l'afficher du wording incompatible
    // a deplacer ailleurs
    public reload() {
        this.updatedCart.emit(true);
    }

    public hasItems(): boolean {
        return this.quote?.items.length > 0;
    }

    public hasOptionSalable(): boolean {
        return this.quote?.option_salable.length > 0;
    }

    public getItems(): JsonProduct[] {
        let products: JsonProduct[] = [];
        if (this.hasItems()) {
            products = Object.values(this.quote.items).map((res) => this.mapItems(res));
        }

        return products;
    }

    public getItemsGencodes() {
        const productsInQuote = [];
        this.getItems().forEach((item) => {
            productsInQuote.push(item.gencode);
        });
        return productsInQuote;
    }

    public getOptionsGencodes() {
        const optionsInQuote = [];
        this.getOptionSalable().forEach((item) => {
            optionsInQuote.push(item.gencode);
        });
        return optionsInQuote;
    }

    public getOptionSalable(): JsonProduct[] {
        let products: JsonProduct[] = [];
        if (this.hasOptionSalable()) {
            products = Object.values(this.quote.option_salable).map((item) => {
                const data = item;
                return data;
            });
        }
        return products;
    }

    /**
     * Vérifie si il y a un plan dans la panier
     *
     * @return boolean
     */
    public hasPlan(): boolean {
        return !!this.getPlan();
    }

    public hasPhone(): boolean {
        return !!this.getPhoneInQuote();
    }

    public getPlanByTypes(types: string[]): JsonProduct | null {
        let plan = null;
        const planType = this.getPlanType();
        types.some((type: string) => {
            if (type === planType) {
                plan = this.getPlan();
                return true;
            }
            return false;
        });
        return plan;
    }

    /**
     * Retourne le plan qui est dans le panier
     *
     * @return JsonProduct | null
     */
    public getPlan(): JsonProduct | null {
        return this.getPlanInQuote() || this.getItemInQuoteByType('faim');
    }

    /**
     * Retourne le plan qui est dans le quote
     *
     * @return JsonProduct | null
     */
    public getPlanInQuote(): JsonProduct | null {
        return this.getItemInQuoteByType('plan');
    }

    /**
     * Vérifie le plan au quote est != du plan scheme
     * si je suis allé au panier et que je change de plan
     *
     * @return boolean
     */
    public hasChangedPlan(): boolean {
        const planScheme = this.getPlan();
        const planInQuote = this.getPlanInQuote();

        return planInQuote && planInQuote.gencode !== planScheme.gencode;
    }

    /**
     * Retourne le phone qui est dans le quote
     *
     * @return JsonProduct | null
     */
    public getPhoneInQuote(): JsonProduct | null {
        return this.getItemInQuoteByType('phone');
    }

    /**
     * Retourne le type mais surtout vérifi si SOWO ou SIMO
     *
     * @return String
     */
    public getPlanType(
        defaultPlans: string[] = [
            Simo.PLAN_TYPE,
            Sowo.PLAN_TYPE,
            Sensation.PLAN_TYPE,
            FaimSensation.PLAN_TYPE,
            Faim.PLAN_TYPE,
        ],
    ): string | null {
        const plan: JsonProduct = this.getPlan();
        let planType: string = null;

        if (plan) {
            planType = plan.type_id;
            if (planType === Sowo.PLAN_TYPE) {
                planType = this.hasObligation(plan) ? Simo.PLAN_TYPE : Sowo.PLAN_TYPE;
            }
            if (!defaultPlans.includes(planType)) {
                return null;
            }
        }
        return planType;
    }

    public isCurrentPlanLocked(): boolean {
        const lockedId = this.configService.getData()?.global?.options.locked;

        const item = this.getPlan();
        // TODO manage locked plan according to checoup lib method
        return (
            !!item && item.params && item.params.related_product && item.params.related_product.indexOf(lockedId) !== -1
        );
    }

    public isPro(): boolean {
        let isPro = false;
        if (this.hasPlan()) {
            const plan = this.getPlan();
            // si scheme
            if (plan.is_pro) {
                isPro = plan.is_pro;
            } else {
                // si quote
                const idRelatedProductPro = this.configService.getData()?.global?.options.pro;
                if (plan.related_product) {
                    isPro = plan.related_product.indexOf(idRelatedProductPro) !== -1;
                }
            }
        }
        return isPro;
    }

    public hasObligation(plan: JsonProduct): boolean {
        const obligation: string = plan.obligation_id || plan.obligation;
        return obligation && obligation !== 'none';
    }

    public edpSelected(): boolean {
        if (this.hasPhone()) {
            return Object.values(this.quote.items)
                .filter((item) => item.source.type && item.source.type.indexOf('phone') !== -1)
                .map((item) => item.params.selectedPayment === 'edp')[0];
        } else {
            return true;
        }
    }

    public isPhoneOnly(): boolean {
        if (this.quote?.items.length === 1) {
            return this.quote.items[0].source.type === PRODUCT_TYPE.PHONE_SIMPLE;
        }
        return false;
    }

    public getConfigurator(): { [key: string]: any } {
        let configurator = Object.assign({}, this.universalStorage.getItem('promoConfigurator'));
        configurator = Object.assign(configurator, this.universalStorage.getItem('flag_eligibility_promo_geoloc'));
        return configurator;
    }

    public setConfigurator(configurator: { [key: string]: any }) {
        return this.universalStorage.setItem('promoConfigurator', configurator, null, true);
    }

    public clean(addQuote: boolean = true): void {
        this.cartModel = new CheckoutCartModel();
        this.cartCheckoutService.refreshType(this.cartModel);
        if (addQuote) {
            this.cartCheckoutService.addQuote(this.cartModel);
        }
        this.cartCheckoutService.save(this.cartModel);
        this.quote = null;
    }

    public setSalesProduct(id: number, isSales?: boolean) {
        const salesProducts = this.universalStorage.getItem('sales_products') || [];
        const index = salesProducts.indexOf(id);
        if (index === -1 && isSales) {
            salesProducts.push(id);
        } else if (index > -1 && !isSales) {
            salesProducts.pop(index);
        }

        this.universalStorage.removeItem('sales_products');
        this.universalStorage.setItem('sales_products', salesProducts);
    }

    public getRenewIdCt(): string {
        if (this.quote?.context?.contractId) {
            return String(this.quote?.context?.contractId);
        }
        return null;
    }

    public getQuoteProductsGencode(): string[] {
        return this.cartCheckoutService
            .getQuote(this.cartModel)
            .products.filter(
                (product) => ProductFactory.Is(product, 'Equipment') || ProductFactory.Is(product, 'Subscription'),
            )
            .map((product) => product.gencode);
    }

    public isInQuote(product: JsonProduct): boolean {
        return this.cartCheckoutService.getQuote(this.cartModel).products.some((p) => p.gencode === product.gencode);
    }

    protected getItemInQuoteByType(type: string): JsonProduct | null {
        if (!this.hasItems()) {
            return null;
        }
        const product: JsonProduct[] = Object.values(this.quote.items)
            .filter((item) => item.source.type && item.source.type.indexOf(type) !== -1)
            .map((res) => this.mapItems(res));
        return product && product.length > 0 ? product[0] : null;
    }

    private mapItems(item) {
        const data = item.source;

        data.params = item.params;
        item.source.entity_id = item.source.id;

        return data;
    }

    private getCheckoutQuote(isMobileQuote: boolean = true): any {
        const currentQuoteContext = this._isRenew ? QUOTE_CONTEXTS.RENEW : QUOTE_CONTEXTS.ACQUISITION;
        const mobileQuoteIndex = this.cartModel.quotes.findIndex((quote) => quote?.context.id === currentQuoteContext);

        let checkoutQuote = this.cartCheckoutService.getQuote(
            this.cartModel,
            isMobileQuote && mobileQuoteIndex >= 0 ? mobileQuoteIndex : 0,
        );
        if (!checkoutQuote) {
            this.cartCheckoutService.addQuote(this.cartModel);
            checkoutQuote = this.cartCheckoutService.getQuote(this.cartModel);
        }

        return checkoutQuote;
    }

    public getCheckoutFaimQuote(): any {
        const checkoutQuote = this.cartModel.quotes.find(
            (quote) => quote?.context.id === QUOTE_CONTEXTS.ACQUISITIONFIX,
        );
        if (checkoutQuote) {
            return checkoutQuote;
        }
        return [];
    }

    /**
     * Retourne le phone qui est dans le quote n'importe quel position dans checkout_cart
     *
     * @return JsonProduct | null
     */
    public getPhoneInQuoteMobile(): JsonProduct | null {
        const currentQuote = this._isRenew ? QUOTE_CONTEXTS.RENEW : QUOTE_CONTEXTS.ACQUISITION;
        const mobileQuote = this.cartModel.quotes.find((quote) => quote?.context.id === currentQuote);
        if (!mobileQuote || mobileQuote?.products.length <= 0) {
            return null;
        }
        const quotePhone = 'phone';
        if (!this.hasItems()) {
            return null;
        }
        const product: JsonProduct[] = Object.values(mobileQuote?.products)
            .filter((item) => item?.data && item?.data?.type_id?.indexOf(quotePhone) !== -1)
            .map((res) => this.mapItemsProduct(res));
        return product && product.length > 0 ? product[0] : null;
    }

    private mapItemsProduct(item) {
        const data = item.data;
        item.data.entity_id = item.data.id;
        return data;
    }

    public addProduct(product): Observable<Product> {
        return this.cartCheckoutService.addProduct(this.cartModel, product);
    }
    public updateProductQty(product, productQty): Observable<Product[]> {
        return this.cartCheckoutService.updateProductQty(this.cartModel, product, productQty);
    }

    public removeProduct(product): Observable<Product[]> {
        return this.cartCheckoutService
            .removeProductsByGencode(this.cartModel, product)
            .pipe(mergeMap(() => this.getFundingObservable(this.cartModel)));
    }

    public getCurrentQuote(): Observable<CartModel> {
        return this.cartCheckoutService.cartModelObs;
    }
    public getCurrentAccessory(): Accessory[] {
        const quote = this.cartCheckoutService.getQuote(this.cartModel);
        return quote.getProductsByType<Accessory>('Accessory');
    }

    private async refreshCart(): Promise<void> {
        this.cartModel = new CheckoutCartModel();
        await this.cartCheckoutService.initCart(this.cartModel);
    }

    public async getCurrentCart(): Promise<CheckoutCartModel> {
        return await lastValueFrom(this.cartCheckoutService.getCart());
    }

    public async addProductsToCart(
        gencodes: string[],
        isFai: boolean = false,
        renewContext: RenewContextService = null,
        isPhoneOnly = false,
        forceAcquisitionContext = false,
    ): Promise<void> {
        this.isUpdatingCartSubj$.next({ updating: true, productsBeingAddedGencodes: gencodes });

        await this.refreshCart();
        const currentCart = await this.getCurrentCart();
        const accessoriesToKeep = [];
        // pour conserver les accessoires au panier dans le cas où l'on choisit un forfait + mobile + accessoires
        // puis que l'on ajoute via la fiche produit un téléphone seul (qui supprime de ce fait le forfait et les accessoires du panier)
        if (isPhoneOnly) {
            this.extractAccessoriesFromMobileQuote(currentCart, accessoriesToKeep);
            this.clean();
        }
        const updatedCart = await lastValueFrom(
            this.cartCheckoutService.addProductsToCart(
                [...gencodes, ...accessoriesToKeep],
                this.cartModel,
                isFai,
                !forceAcquisitionContext && (renewContext?.contractId || this.getRenewContractIdFromStorage()),
            ),
        );

        await this.refreshCart();
        await lastValueFrom(this.getFundingObservable(updatedCart));

        this.quote = this.getQuote();
        this.isUpdatingCartSubj$.next({ updating: false, productsBeingAddedGencodes: [] });
    }

    private getFundingObservable(cart: CheckoutCartModel): Observable<Product[]> {
        let obs: Observable<any> = of(null);
        const fundingStorage = sessionStorage.getItem('funding_key') as FUNDING_ENUM;
        if (fundingStorage) {
            obs = this.addFundingService.setFundingDefaultValue(cart, fundingStorage).pipe(catchError(() => of(null)));
        }
        return obs;
    }

    private extractAccessoriesFromMobileQuote(currentCart: CartModel, accessoriesToKeep: string[]): void {
        currentCart?.quotes
            ?.find((quote) => quote.context.id === QUOTE_CONTEXTS.ACQUISITION)
            ?.products?.filter((p) => ProductFactory.Is(p, 'Accessory'))
            ?.forEach((p) => accessoriesToKeep.push(p.gencode));
    }

    private getRenewContractIdFromStorage(): string {
        const renewalContextQuoteId = this.cartModel.quotes.findIndex(
            (quote) => quote?.context.id === QUOTE_CONTEXTS.RENEW,
        );

        const cartModelAny = this.cartModel as unknown as any;
        if (renewalContextQuoteId >= 0) {
            return cartModelAny.quotes?.[renewalContextQuoteId]?.context?.contractId;
        }

        return undefined;
    }
}
